import { useEffect, useRef, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  User, Key, Monitor, LogOut, Loader2, AlertCircle, CheckCircle,
  ArrowRightLeft, Globe, Apple, BadgeCheck, ShieldAlert, Clock, X, Gift, Languages,
  CreditCard, ChevronRight, Sparkles, Cloud as CloudIcon, Download, Mail, Moon,
  Eye, EyeOff, Copy, Check,
} from 'lucide-react';
import { useCloud } from '../hooks/useCloud';
import { useEnginePack } from '../hooks/useEnginePack';
import { isDesktop } from '@/lib/fileSystem';
import { formatBytes } from '../lib/billing';
import { useAuth } from '../hooks/useAuth';
import { signOut, getIdToken } from '../lib/auth';
import { setTier } from '../lib/license';
import { hasValidLicense, getLicenseClaims, verifyStoredLicense, clearLicense } from '../lib/licenseToken';
import ActivationModal from '../components/ActivationModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeSwitcher from '../components/ThemeSwitcher';
import DesktopAccount from '@/components/desktop/DesktopAccount';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Account() {
  if (isDesktop()) return <DesktopAccount />;
  return <WebAccount />;
}

function WebAccount() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, account, loading, setAccount } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activationOpen, setActivationOpen] = useState(false);
  const [transferState, setTransferState] = useState(null);
  const [cancellingCode, setCancellingCode] = useState(null);
  const [codeRevealed, setCodeRevealed] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [incomingBusy, setIncomingBusy] = useState({});
  const [declineConfirm, setDeclineConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [sessionSigningOut, setSessionSigningOut] = useState({}); // { [sessionId]: true }
  const [resendingOtp, setResendingOtp] = useState(false);
  const [savingOptOut, setSavingOptOut] = useState(false);
  // Offline-license card (desktop only): claims of the locally-verified token.
  const [offlineLicense, setOfflineLicense] = useState(null);
  const [deactivateState, setDeactivateState] = useState('idle'); // idle | confirm | busy | error

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!loading && !user) navigate('/login?next=/account', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!isDesktop()) return;
    verifyStoredLicense().then((s) => setOfflineLicense(s.valid ? s.claims : null));
  }, []);

  // Belt-and-suspenders: always refetch on mount when the user is present,
  // since useAuth.account is set once at sign-in and may be stale after
  // activation / Stripe checkout / transfers happening elsewhere.
  useEffect(() => {
    if (!loading && user) {
      refresh().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.uid]);

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load account');
      const data = await res.json();
      setAccount(data);
      if (data.tier) setTier(data.tier);
      const cached = JSON.parse(localStorage.getItem('scrivar_session') || 'null') || {};
      localStorage.setItem('scrivar_session', JSON.stringify({ ...cached, ...data }));
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    // A licensed device keeps working signed-out (logout-capable activation) —
    // don't dump the user on the login form as if they were locked out.
    navigate(hasValidLicense() ? '/tools' : '/login');
  }

  async function deactivateThisDevice() {
    setDeactivateState('busy');
    try {
      const fingerprint = await window.electronAPI?.getDeviceFingerprint?.();
      if (!fingerprint) throw new Error('Could not read this device.');
      const token = await getIdToken();
      const res = await fetch(`${API}/license/deactivate-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deviceFingerprint: fingerprint }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Could not deactivate this device.');
      }
      await clearLicense();
      setOfflineLicense(null);
      setDeactivateState('idle');
      setToast({ kind: 'success', text: 'Device deactivated. Sign in on another computer to use your license there.' });
    } catch (err) {
      console.error('deactivate-device failed:', err);
      setDeactivateState('error');
    }
  }

  function startTransfer(code) {
    setTransferState({ code, phase: 'destination', toEmail: '', loading: false });
  }

  async function submitDestination() {
    const destination = (transferState.toEmail || '').trim().toLowerCase();
    if (!EMAIL_REGEX.test(destination)) {
      setTransferState((s) => ({ ...s, error: 'Please enter a valid email address.' }));
      return;
    }
    if (destination === (account.email || '').toLowerCase()) {
      setTransferState((s) => ({ ...s, error: "You can't transfer a code to your own account." }));
      return;
    }

    setTransferState((s) => ({ ...s, loading: true, error: null }));
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/transfer-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: transferState.code, toEmail: destination }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Transfer request failed');
      setTransferState({
        code: transferState.code,
        toEmail: destination,
        phase: 'confirm',
        otp: '',
        // devOtp is only meaningful in development; never store or show it
        // in a production build. Belt-and-braces: the server also strips
        // devOtp when NODE_ENV === 'production'.
        devOtp: import.meta.env.DEV ? data.devOtp : undefined,
        loading: false,
      });
    } catch (err) {
      setTransferState((s) => ({ ...s, loading: false, error: err.message }));
    }
  }

  async function confirmTransfer() {
    setTransferState((s) => ({ ...s, loading: true, error: null }));
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/confirm-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: transferState.code, otp: transferState.otp }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Transfer confirmation failed');
      setTransferState(null);
      await refresh();
    } catch (err) {
      setTransferState((s) => ({ ...s, loading: false, error: err.message }));
    }
  }

  async function acceptIncoming(transfer) {
    const id = transfer.pendingTransferId;
    setIncomingBusy((b) => ({ ...b, [id]: 'accepting' }));
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/accept-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pendingTransferId: id }),
      });
      const data = await res.json();
      if (!data.success) {
        // Server returns a generic 'not available' message for mismatch or
        // expiry — surface something clearer when it's plausibly an expiry.
        const msg = /not available/i.test(data.message || '') && transfer.expiresAt
          && new Date(transfer.expiresAt) <= new Date()
          ? 'This transfer has expired.'
          : (data.message || 'Could not accept transfer.');
        throw new Error(msg);
      }
      setToast({ kind: 'success', text: 'Code activated — welcome to Pro.' });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setIncomingBusy((b) => {
        const next = { ...b };
        delete next[id];
        return next;
      });
    }
  }

  async function declineIncomingConfirmed(transfer) {
    const id = transfer.pendingTransferId;
    setIncomingBusy((b) => ({ ...b, [id]: 'declining' }));
    setError(null);
    setDeclineConfirm(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/decline-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pendingTransferId: id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not decline transfer.');
      setToast({ kind: 'success', text: 'Transfer declined.' });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setIncomingBusy((b) => {
        const next = { ...b };
        delete next[id];
        return next;
      });
    }
  }

  async function resendTransferOtp(code) {
    setResendingOtp(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/transfer-code/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not resend confirmation code.');
      setToast({ kind: 'success', text: 'Confirmation code re-sent.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setResendingOtp(false);
    }
  }

  async function resendPendingNotification(code) {
    setResendingOtp(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/transfer-notification/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not resend notification.');
      setToast({ kind: 'success', text: 'Notification re-sent to the recipient.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setResendingOtp(false);
    }
  }

  async function killSession(sessionId) {
    setSessionSigningOut((s) => ({ ...s, [sessionId]: true }));
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not sign out that session.');
      setToast({ kind: 'success', text: 'Signed out of that device.' });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSessionSigningOut((s) => {
        const next = { ...s };
        delete next[sessionId];
        return next;
      });
    }
  }

  async function cancelPendingTransfer(code) {
    setCancellingCode(code);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account/cancel-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not cancel transfer.');
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancellingCode(null);
    }
  }

  function maskCode(code) {
    if (!code || code.length < 19) return code;
    return `SCRV-\u2022\u2022\u2022\u2022-\u2022\u2022\u2022\u2022-${code.slice(-4)}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" aria-label="Loading" />
      </div>
    );
  }

  // Auth state resolved but no user — the useEffect on line 41-43 has already
  // dispatched a redirect to /login; render nothing for that one frame.
  if (!user) return null;

  // Signed in, but the /account fetch hasn't populated yet (or failed). Show
  // an explicit state instead of rendering blank below the trial banner.
  // Bug 2: a stale "useAuth.account = null" would silently bail here, so the
  // page would look completely empty in production after any transient API
  // error or CORS-rejected fetch (see api/middleware/cors.js).
  if (!account) {
    if (error) {
      return (
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-base font-semibold text-gray-900 mb-1">Couldn't load your account</h1>
          <p className="text-sm text-gray-600 mb-4 break-words">{error}</p>
          <button
            type="button"
            onClick={() => refresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
          >
            {refreshing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Retrying…
              </>
            ) : (
              'Try again'
            )}
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" aria-label="Loading account" />
      </div>
    );
  }

  // A code is "displayed" if it's activated to this account OR pending transfer
  // from this account. Pending codes still show — the owner can cancel.
  const ownedCode = account.codes?.find(
    (c) => c.status === 'activated' || c.status === 'pending_transfer'
  );
  const tier = account.tier || 'free';
  const tierColors = {
    free: 'bg-gray-100 text-gray-700 border-gray-200',
    lite: 'bg-blue-50 text-blue-700 border-blue-200',
    pro: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    lifetime_lite: 'bg-amber-50 text-amber-800 border-amber-200',
    lifetime_pro: 'bg-amber-50 text-amber-900 border-amber-200',
  };
  const tierLabel = {
    free: 'Free',
    lite: 'Lite',
    pro: 'Pro',
    lifetime_lite: 'Lite (Lifetime)',
    lifetime_pro: 'Pro (Lifetime)',
  };
  const isProTier = tier === 'pro' || tier === 'lifetime_pro';
  const hasAddon = Boolean(account.addonSubscription);

  const hasWebAndDesktopSession = account.sessions?.some((s) => s.platform === 'web')
    && account.sessions?.some((s) => ['win', 'mac'].includes(s.platform));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">My Account</h1>
      <p className="text-sm text-gray-500 mb-6 sm:mb-8">Manage your profile, license, and active sessions.</p>

      {error && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Profile ─────────────────────────────────────────────── */}
      <SectionCard icon={User} title="Profile" description="Your Scrivar account details.">
        <Row label="Email">
          <span className="flex items-center gap-1.5">
            {account.email}
            {user.emailVerified ? (
              <BadgeCheck className="w-4 h-4 text-green-500" aria-label="Verified" />
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                <ShieldAlert className="w-3 h-3" aria-hidden="true" />
                Unverified
              </span>
            )}
          </span>
        </Row>
        <Row label="Member since">
          {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : '\u2014'}
        </Row>
        <ProfileEditor
          account={account}
          onSaved={() => { refresh(); setToast({ kind: 'success', text: 'Profile updated.' }); }}
          onError={(msg) => setError(msg)}
        />
        <div className="pt-4 flex justify-end">
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            {t('common:buttons.sign_out')}
          </button>
        </div>
      </SectionCard>

      {/* ── Subscription ────────────────────────────────────────── */}
      <SectionCard
        icon={CreditCard}
        title="Subscription & billing"
        description="Manage your plan, update payment details, or upgrade."
      >
        <div className="pt-1 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Current plan:</span>
            <span className="font-semibold">
              {tierLabel[tier] || 'Free'}
              {hasAddon && ' + Cloud & AI'}
            </span>
            {isProTier && (
              <Sparkles className="w-4 h-4 text-indigo-600" aria-hidden="true" />
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate('/account/billing')}
            className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700"
          >
            Manage billing
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </SectionCard>

      {/* ── Cloud storage ───────────────────────────────────────── */}
      {(account.capabilities?.cloud_bytes || 0) > 0 && (
        <CloudStorageSection />
      )}

      {/* ── Language ────────────────────────────────────────────── */}
      <SectionCard
        icon={Languages}
        title={t('auth:account.language_section')}
        description={t('auth:account.language_hint')}
      >
        <div className="pt-1">
          <LanguageSwitcher
            onChange={async (code) => {
              // Fire-and-forget: local change is immediate; server sync is best-effort.
              try {
                const token = await getIdToken();
                await fetch(`${API}/account`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ preferred_language: code }),
                });
              } catch {
                // Offline or unauthenticated — local change still applies.
              }
            }}
          />
        </div>
      </SectionCard>

      {/* ── Appearance ──────────────────────────────────────────── */}
      <SectionCard
        icon={Moon}
        title={t('auth:account.appearance_section', 'Appearance')}
        description={t('auth:account.appearance_hint', 'Choose how Scrivar looks. System follows your device setting.')}
      >
        <div className="pt-1">
          <ThemeSwitcher />
        </div>
      </SectionCard>

      {/* ── Notifications ───────────────────────────────────────── */}
      <SectionCard
        icon={Mail}
        title="Notifications"
        description="Choose which emails you receive from Scrivar."
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
          <label style={{ fontSize: 14 }}>
            <input
              type="checkbox"
              checked={!account?.announcementsOptOut}
              disabled={savingOptOut}
              onChange={async (e) => {
                const optOut = !e.target.checked;
                setSavingOptOut(true);
                try {
                  const token = await getIdToken();
                  const res = await fetch(`${API}/account/announcements-opt-out`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ optOut }),
                  });
                  const data = await res.json();
                  if (!data.success) throw new Error(data.message || 'Could not update notification preference.');
                  setAccount((prev) => ({ ...(prev || {}), announcementsOptOut: optOut }));
                } catch (err) {
                  setError(err.message);
                } finally {
                  setSavingOptOut(false);
                }
              }}
            />{' '}Receive product announcement emails
          </label>
        </div>
      </SectionCard>

      {/* ── Incoming transfers ──────────────────────────────────── */}
      {(account.incomingTransfers || []).length > 0 && (
        <SectionCard
          icon={Gift}
          title="Incoming transfer"
          description="Someone sent you a Scrivar Pro code."
        >
          <ul className="space-y-3">
            {account.incomingTransfers.map((t) => (
              <IncomingTransferRow
                key={t.pendingTransferId}
                transfer={t}
                busy={incomingBusy[t.pendingTransferId]}
                onAccept={() => acceptIncoming(t)}
                onDecline={() => setDeclineConfirm(t)}
              />
            ))}
          </ul>
        </SectionCard>
      )}

      {/* ── Subscription & Code ──────────────────────────────────── */}
      <SectionCard icon={Key} title="Subscription &amp; Code" description="Your registration code and plan tier.">
        <div className="flex items-center gap-3 mb-4">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${tierColors[tier] || tierColors.free}`}
          >
            {tierLabel[tier] || 'Free'}
          </span>
          {ownedCode && (
            <>
              <span className="text-sm font-mono text-gray-600">
                {codeRevealed ? ownedCode.code : maskCode(ownedCode.code)}
              </span>
              <button
                type="button"
                onClick={() => setCodeRevealed((v) => !v)}
                className="p-1 rounded text-gray-400 hover:text-gray-700"
                aria-label={codeRevealed ? 'Hide code' : 'Show full code'}
                title={codeRevealed ? 'Hide code' : 'Show full code'}
              >
                {codeRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(ownedCode.code);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  } catch { setCodeRevealed(true); }
                }}
                className="p-1 rounded text-gray-400 hover:text-gray-700"
                aria-label="Copy code"
                title={codeCopied ? 'Copied' : 'Copy code'}
              >
                {codeCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>

        {ownedCode ? (
          <>
            <div className="text-xs text-gray-500 space-y-1 mb-4">
              {ownedCode.activated_at && (
                <div>
                  Activated: {new Date(ownedCode.activated_at).toLocaleDateString()}
                </div>
              )}
            </div>

            {ownedCode.status === 'pending_transfer' && ownedCode.pendingTransfer ? (
              <PendingTransferBanner
                pending={ownedCode.pendingTransfer}
                onCancel={() => cancelPendingTransfer(ownedCode.code)}
                cancelling={cancellingCode === ownedCode.code}
                onResend={() => resendPendingNotification(ownedCode.code)}
                resending={resendingOtp}
              />
            ) : (
              <TransferPanel
                ownedCode={ownedCode}
                currentEmail={account.email}
                transferState={transferState}
                onStart={() => startTransfer(ownedCode.code)}
                onCancel={() => setTransferState(null)}
                onChangeEmail={(toEmail) => setTransferState((s) => ({ ...s, toEmail, error: null }))}
                onSubmitDestination={submitDestination}
                onChangeOtp={(otp) => setTransferState((s) => ({ ...s, otp, error: null }))}
                onConfirm={confirmTransfer}
                onResend={() => resendTransferOtp(ownedCode.code)}
                resending={resendingOtp}
              />
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => setActivationOpen(true)}
            className="text-sm px-3 py-1.5 bg-brand-blue text-white rounded-lg hover:bg-brand-dark"
          >
            Enter your registration code
          </button>
        )}
      </SectionCard>

      {/* ── Offline license (desktop, logout-capable activation) ──── */}
      {isDesktop() && offlineLicense && (
        <SectionCard
          icon={BadgeCheck}
          title="Offline license — this device"
          description="This computer can use the full PDF toolset while signed out, even offline."
        >
          <p className="text-sm text-gray-600">
            {offlineLicense.tier === 'pro' ? 'Pro' : 'Lite'} lifetime license
            {offlineLicense.accountEmail ? ` · ${offlineLicense.accountEmail}` : ''}.
            Deactivate to move the license to another computer.
          </p>
          {deactivateState === 'error' && (
            <p className="text-sm text-red-600 mt-2" role="alert">
              Could not deactivate this device. Check your connection and try again.
            </p>
          )}
          <div className="mt-3">
            {deactivateState === 'confirm' ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={deactivateThisDevice}
                  className="rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700"
                >
                  Yes, deactivate this device
                </button>
                <button
                  type="button"
                  onClick={() => setDeactivateState('idle')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={deactivateState === 'busy'}
                onClick={() => setDeactivateState('confirm')}
                className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {deactivateState === 'busy' ? 'Deactivating…' : 'Deactivate this device'}
              </button>
            )}
          </div>
        </SectionCard>
      )}

      {/* ── Devices & Sessions ───────────────────────────────────── */}
      <SectionCard icon={Monitor} title="Devices &amp; Sessions" description="Where you're currently signed in.">
        {account.sessions && account.sessions.length > 0 ? (
          <>
            <ul className="space-y-2" aria-label="Active sessions">
              {account.sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  onSignOut={() => killSession(s.id)}
                  signingOut={!!sessionSigningOut[s.id]}
                  onSignOutCurrent={handleSignOut}
                />
              ))}
            </ul>
            {hasWebAndDesktopSession && (
              <p className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                You have an active web session and a desktop session. Only one of each is allowed at a time.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">No active sessions.</p>
        )}
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="mt-4 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          {refreshing ? 'Refreshing\u2026' : 'Refresh'}
        </button>
      </SectionCard>

      {/* ── Offline mode (desktop only) ──────────────────────────── */}
      {isDesktop() && <OfflineEnginesCard />}

      <ActivationModal
        open={activationOpen}
        onClose={() => setActivationOpen(false)}
        onActivated={() => { setActivationOpen(false); refresh(); }}
      />

      {declineConfirm && (
        <DeclineConfirmModal
          transfer={declineConfirm}
          onCancel={() => setDeclineConfirm(null)}
          onConfirm={() => declineIncomingConfirmed(declineConfirm)}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2
            ${toast.kind === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-gray-900 text-white'}`}
        >
          {toast.kind === 'success' && <CheckCircle className="w-4 h-4" aria-hidden="true" />}
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}

// ─── Incoming transfer row ───────────────────────────────────────────────────

function IncomingTransferRow({ transfer, busy, onAccept, onDecline }) {
  const expiresAt = transfer.expiresAt ? new Date(transfer.expiresAt) : null;

  // `now` is state-owned so render is pure. Tick once per second while there's
  // an expiry — the row lives inside an already-rendered incoming-transfer
  // card, so the extra timer is scoped to visible transfers only.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [expiresAt]);

  const nearExpiry = !!(expiresAt && (expiresAt.getTime() - now) <= 60000);

  const expiresLabel = expiresAt
    ? nearExpiry
      ? formatSecondsRemaining(expiresAt, now)
      : `Expires ${formatRelativeFuture(expiresAt, now)}`
    : '';

  const tierLabel = transfer.tier === 'pro' ? 'Scrivar Pro' : 'Scrivar';
  const busyLabel = busy === 'accepting' ? 'Accepting…' : busy === 'declining' ? 'Declining…' : null;

  return (
    <li className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-4">
      <div className="text-sm text-gray-900">
        <span className="font-medium">{transfer.fromEmail || 'Someone'}</span>{' '}
        wants to transfer a <span className="font-medium">{tierLabel}</span> code to you.
      </div>
      {expiresAt && (
        <div className={`mt-1 text-xs ${nearExpiry ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
          {expiresLabel}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy === 'accepting' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              {busyLabel}
            </>
          ) : (
            'Accept code'
          )}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={!!busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'declining' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              {busyLabel}
            </>
          ) : (
            'Decline'
          )}
        </button>
      </div>
    </li>
  );
}

function formatSecondsRemaining(date, now = Date.now()) {
  const secs = Math.max(0, Math.floor((date.getTime() - now) / 1000));
  if (secs === 0) return 'Expiring now';
  return `Expires in ${secs}s`;
}

// ─── Decline confirm modal ───────────────────────────────────────────────────

function DeclineConfirmModal({ transfer, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    const prev = document.activeElement;
    cancelRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = [cancelRef.current, confirmRef.current].filter(Boolean);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      prev?.focus?.();
    };
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="decline-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="decline-title" className="text-base font-semibold text-gray-900 mb-1">
          Decline this transfer?
        </h3>
        <p className="text-sm text-gray-600">
          The code from <span className="font-medium">{transfer.fromEmail}</span> will be returned
          to the sender.
        </p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Decline transfer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function CloudStorageSection() {
  const { usage, loading, error } = useCloud({ autoLoad: true });
  const used = usage?.usedBytes || 0;
  const limit = usage?.limitBytes || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const warning = pct >= 80;

  return (
    <SectionCard
      icon={CloudIcon}
      title="Cloud storage"
      description="Files you've saved to Scrivar Cloud."
    >
      {loading && !usage ? (
        <div className="pt-2 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : error && !usage ? (
        <div className="pt-2 text-sm text-gray-500">
          Couldn't load your cloud storage — you may be offline. Your saved files are safe.
        </div>
      ) : (
        <div className="pt-1">
          <div className="flex items-baseline justify-between text-xs text-gray-600 mb-1">
            <span>
              {formatBytes(used)} of {formatBytes(limit)}
              {usage?.documentCount != null && ` · ${usage.documentCount} doc${usage.documentCount === 1 ? '' : 's'}`}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded bg-gray-100 overflow-hidden">
            <div className={`h-2 ${warning ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <a href="/cloud" className="text-sm text-indigo-600 hover:underline">Open Cloud</a>
            {warning && (
              <a href="/account/billing" className="text-xs text-amber-800 hover:underline">
                Upgrade for more storage
              </a>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Offline mode (desktop opt-in engine pack) ───────────────────────────────
// No internal tool names surfaced — just "offline engines". Cloud-first stays
// the default when online; this is the opt-in for offline / not-signed-in use.
function OfflineEnginesCard() {
  const { status, loading, installing, progress, error, install } = useEnginePack();

  const installed = status?.installed;
  const updateAvailable = status?.updateAvailable;
  const pct = progress?.phase === 'download' ? (progress.percent || 0) : (installing ? 100 : 0);
  const phaseLabel = progress?.phase === 'download'
    ? `Downloading… ${progress.percent || 0}%`
    : progress?.phase === 'extract'
      ? 'Installing…'
      : installing ? 'Preparing…' : '';

  return (
    <SectionCard
      icon={Download}
      title="Offline mode"
      description="Compress, OCR, and convert on your computer — no internet connection needed."
    >
      {loading && !status ? (
        <div className="pt-1 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Checking…
        </div>
      ) : installing ? (
        <div className="pt-1">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{phaseLabel}</span>
            {progress?.phase === 'download' && <span>{progress.percent || 0}%</span>}
          </div>
          <div className="h-2 rounded bg-gray-100 overflow-hidden">
            <div className="h-2 bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-gray-400">
            One-time download (about 120&nbsp;MB). You can keep working while it finishes.
          </p>
        </div>
      ) : installed ? (
        <div className="pt-1">
          <div className="flex items-center gap-2 text-sm text-gray-800">
            <CheckCircle className="w-4 h-4 text-green-500 shrink-0" aria-hidden="true" />
            Offline mode is ready.
          </div>
          {updateAvailable && (
            <button
              type="button"
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              Update offline engines
            </button>
          )}
        </div>
      ) : (
        <div className="pt-1">
          <p className="text-sm text-gray-600 mb-3">
            Download once to use heavy tools without a connection. While you're online, these run
            securely in the cloud automatically.
          </p>
          <button
            type="button"
            onClick={install}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700"
          >
            <Download className="w-4 h-4" aria-hidden="true" />
            Download offline engines
          </button>
          <p className="mt-2 text-xs text-gray-400">About 120&nbsp;MB, one-time.</p>
        </div>
      )}

      {error && (
        <div role="alert" className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </SectionCard>
  );
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 sm:p-6 mb-4 sm:mb-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-gray-400" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Profile editor (name + company) ─────────────────────────────────────────

function ProfileEditor({ account, onSaved, onError }) {
  const initialFirst = account.firstName || '';
  const initialLast = account.lastName || '';
  const initialCompany = account.companyName || '';

  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [companyName, setCompanyName] = useState(initialCompany);
  const [saving, setSaving] = useState(false);

  // Re-seed local state whenever the parent account refreshes (e.g. after a
  // successful save or a background reload). Without this, after Save the
  // inputs would still show the pre-save values until a remount.
  useEffect(() => {
    setFirstName(account.firstName || '');
    setLastName(account.lastName || '');
    setCompanyName(account.companyName || '');
  }, [account.firstName, account.lastName, account.companyName]);

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const trimmedCompany = companyName.trim();

  const dirty =
    trimmedFirst !== (initialFirst || '').trim() ||
    trimmedLast !== (initialLast || '').trim() ||
    trimmedCompany !== (initialCompany || '').trim();

  const canSave = !!trimmedFirst && dirty && !saving;

  async function handleSave(e) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          first_name: trimmedFirst,
          last_name: trimmedLast,
          company_name: trimmedCompany,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data?.message || 'Could not save profile.');
      }
      if (onSaved) onSaved();
    } catch (err) {
      if (onError) onError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave} className="pt-2 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="profile-first" className="block text-xs font-medium text-gray-600 mb-1">
            First name <span className="text-red-500">*</span>
          </label>
          <input
            id="profile-first"
            type="text"
            required
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            maxLength={100}
            disabled={saving}
            className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
          />
        </div>
        <div>
          <label htmlFor="profile-last" className="block text-xs font-medium text-gray-600 mb-1">
            Last name
          </label>
          <input
            id="profile-last"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            maxLength={100}
            disabled={saving}
            className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
          />
        </div>
      </div>
      <div>
        <label htmlFor="profile-company" className="block text-xs font-medium text-gray-600 mb-1">
          Company
        </label>
        <input
          id="profile-company"
          type="text"
          autoComplete="organization"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Company (optional)"
          maxLength={200}
          disabled={saving}
          className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!canSave}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px]"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Saving…
            </>
          ) : (
            'Save changes'
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 text-right break-all">{children}</span>
    </div>
  );
}

// ─── Session row ──────────────────────────────────────────────────────────────

function SessionRow({ session, onSignOut, signingOut, onSignOutCurrent }) {
  const Icon = session.platform === 'web' ? Globe
             : session.platform === 'mac' ? Apple
             : Monitor;
  const label = session.platform === 'web' ? 'Web browser'
              : session.platform === 'mac' ? 'macOS desktop'
              : session.platform === 'win' ? 'Windows desktop'
              : session.platform;

  const lastActive = session.last_active
    ? formatRelativeTime(new Date(session.last_active))
    : '\u2014';

  const cached = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
  const isCurrent = cached?.token && session.session_token && cached.token === session.session_token;

  return (
    <li className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
      <Icon className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 flex items-center gap-2 flex-wrap">
          {label}
          {isCurrent && (
            <span className="text-[10px] uppercase tracking-wide text-brand-blue bg-brand-light/50 px-1.5 py-0.5 rounded">
              This device
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">Last active {lastActive}</div>
      </div>
      <button
        type="button"
        onClick={isCurrent ? onSignOutCurrent : onSignOut}
        disabled={signingOut}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-700 border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50 min-h-[36px]"
        aria-label={isCurrent ? 'Sign out of this device' : `Sign out of ${label}`}
      >
        {signingOut ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        <span>Sign out</span>
      </button>
    </li>
  );
}

function formatRelativeTime(date) {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return date.toLocaleDateString();
}

function formatRelativeFuture(date, now = Date.now()) {
  const diff = date.getTime() - now;
  if (diff <= 0) return 'any moment';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'in 1 day';
  return `in ${days} days`;
}

// ─── Pending transfer banner ──────────────────────────────────────────────────

function PendingTransferBanner({ pending, onCancel, cancelling, onResend, resending }) {
  const expiresDate = pending.expiresAt ? new Date(pending.expiresAt) : null;
  const expiresRelative = expiresDate ? formatRelativeFuture(expiresDate) : 'soon';
  const expiresAbsolute = expiresDate ? expiresDate.toLocaleString() : '';

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" aria-hidden="true" />
        <div className="text-sm text-amber-900 flex-1">
          <div className="font-medium">Transfer pending</div>
          <div className="mt-1">
            Sent to <span className="font-medium">{pending.toEmail}</span>. Expires {expiresRelative}
            {expiresAbsolute && (
              <span className="text-amber-700"> ({expiresAbsolute})</span>
            )}.
          </div>
          <div className="mt-1 text-amber-700 text-xs">
            Your plan stays active until the recipient claims the code. If they don't claim it, the code
            returns to your account automatically.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {onResend && (
          <button
            type="button"
            onClick={onResend}
            disabled={resending || cancelling}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-amber-300 bg-white text-amber-900 rounded-lg hover:bg-amber-100 disabled:opacity-50"
          >
            {resending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Resending&hellip;
              </>
            ) : (
              'Resend notification'
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling || resending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-amber-300 bg-white text-amber-900 rounded-lg hover:bg-amber-100 disabled:opacity-50"
        >
          {cancelling ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Cancelling&hellip;
            </>
          ) : (
            <>
              <X className="w-4 h-4" aria-hidden="true" />
              Cancel transfer
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Transfer panel ───────────────────────────────────────────────────────────

function TransferPanel({
  ownedCode, currentEmail, transferState,
  onStart, onCancel, onChangeEmail, onSubmitDestination, onChangeOtp, onConfirm,
  onResend, resending,
}) {
  const transfersRemaining = ownedCode.transfersRemaining ?? 3;

  if (transfersRemaining === 0) {
    return (
      <p className="text-xs text-gray-500 italic">
        This code has reached the maximum of 3 transfers. Contact support for further changes.
      </p>
    );
  }

  if (!transferState) {
    return (
      <div>
        <button
          type="button"
          onClick={onStart}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
        >
          <ArrowRightLeft className="w-4 h-4" aria-hidden="true" />
          Transfer this code to a different email
        </button>
        <p className="mt-2 text-xs text-gray-500">
          Transfers remaining: {transfersRemaining}
        </p>
      </div>
    );
  }

  if (transferState.phase === 'destination') {
    const destination = transferState.toEmail || '';
    const selfTransfer = EMAIL_REGEX.test(destination)
      && destination.trim().toLowerCase() === (currentEmail || '').toLowerCase();
    const valid = EMAIL_REGEX.test(destination) && !selfTransfer;

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <label htmlFor="transfer-destination" className="block text-sm font-medium text-gray-800 mb-1">
            Transfer to email
          </label>
          <p className="text-xs text-gray-500 mb-2">
            The code will only be claimable by an account signed in with this email address.
          </p>
          <input
            id="transfer-destination"
            type="email"
            autoComplete="email"
            value={destination}
            onChange={(e) => onChangeEmail(e.target.value)}
            placeholder="newowner@example.com"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm"
            disabled={transferState.loading}
          />
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2.5 text-sm text-gray-600 hover:text-gray-800 min-h-[44px]"
            disabled={transferState.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmitDestination}
            disabled={!valid || transferState.loading}
            className="px-4 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 inline-flex items-center gap-2 min-h-[44px]"
          >
            {transferState.loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Sending&hellip;
              </>
            ) : (
              'Send confirmation code'
            )}
          </button>
        </div>
        {transferState.error && (
          <div
            role="alert"
            className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span>{transferState.error}</span>
          </div>
        )}
      </div>
    );
  }

  if (transferState.phase === 'confirm') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
        <p className="text-sm text-amber-900">
          We sent a 6-digit confirmation code to <span className="font-medium">{currentEmail}</span>. Enter it below to
          lock the transfer to <span className="font-medium">{transferState.toEmail}</span>.
        </p>
        {import.meta.env.DEV && transferState.devOtp && (
          <p className="text-xs text-amber-800 bg-amber-100 rounded px-2 py-1 font-mono">
            Dev mode OTP: {transferState.devOtp}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <label htmlFor="transfer-otp" className="sr-only">6-digit confirmation code</label>
          <input
            id="transfer-otp"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={transferState.otp || ''}
            onChange={(e) => onChangeOtp(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="flex-1 min-w-0 px-3 py-2.5 border border-amber-300 rounded-lg text-center font-mono tracking-wider text-base sm:text-sm"
            aria-describedby={transferState.error ? 'transfer-error' : undefined}
          />
          <button
            type="button"
            onClick={onConfirm}
            disabled={transferState.loading || (transferState.otp || '').length !== 6}
            className="px-4 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 min-h-[44px]"
          >
            {transferState.loading ? 'Confirming\u2026' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2.5 text-sm text-gray-600 hover:text-gray-800 min-h-[44px]"
          >
            Cancel
          </button>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-amber-800">Didn't get it? Check spam, or</span>
          <button
            type="button"
            onClick={onResend}
            disabled={resending || transferState.loading}
            className="text-amber-900 underline decoration-dotted hover:no-underline disabled:opacity-50"
          >
            {resending ? 'Resending\u2026' : 'Resend code'}
          </button>
        </div>
        {transferState.error && (
          <div
            id="transfer-error"
            role="alert"
            className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span>{transferState.error}</span>
          </div>
        )}
      </div>
    );
  }

  if (transferState.phase === 'done') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
        <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 shrink-0" aria-hidden="true" />
        <div className="text-sm text-green-800">Transfer pending. Refreshing&hellip;</div>
      </div>
    );
  }

  return null;
}
